import { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner, Text } from "@chakra-ui/react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import { cn } from "utils/helpers";
import { formatCurrency, TOTAL_ROWS, TotalType } from "./utils";
import type { SummaryTableData } from "../../Submission/types/quote.type";
import type { QuoteTableMode } from "./quote-table.types";
import { getSummaryTableData, deleteSummaryTable } from "../../api";
import type { QuoteResourceType } from "../../api/procurer-quotes-api";

export interface SummaryTableProps {
	categoryNames: string[];
	includeColumnTotals?: boolean;
	inviteId?: number;
	/** Live cell values override API data. Shape: { [quoteTableId]: { [columnName]: total } } */
	liveCellValues?: Record<number, Record<string, number>>;
	mode?: QuoteTableMode;
	quoteTableIds: number[];
	quoteTableNames: Record<number, string>;
	/**
	 * Resource this summary table belongs to. Determines the API route used
	 * for data-fetch and delete (`rfx/{id}` vs `rfx-template/{id}`). Defaults
	 * to `"rfx"` for backward compatibility with the live-quote flow.
	 */
	resourceType?: QuoteResourceType;
	rfxId: number;
	summaryTableId: number;
	summaryTableName: string;
	onDelete?: () => void;
	onEdit?: () => void;
}

const cellBorder = "border border-[#EAECF0]";

const SummaryTable: React.FC<SummaryTableProps> = ({
	categoryNames,
	includeColumnTotals = false,
	inviteId,
	liveCellValues,
	mode,
	onDelete,
	onEdit,
	quoteTableIds,
	quoteTableNames,
	resourceType = "rfx",
	rfxId,
	summaryTableId,
	summaryTableName,
}) => {
	const isProcurerView = mode === 'edit';
	const [data, setData] = useState<SummaryTableData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await getSummaryTableData(rfxId, summaryTableId, inviteId, resourceType);
			setData(result);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, [rfxId, summaryTableId, inviteId, resourceType]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleDelete = useCallback(async () => {
		setIsDeleting(true);
		try {
			await deleteSummaryTable(rfxId, summaryTableId, resourceType);
			onDelete?.();
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
		}
	}, [rfxId, summaryTableId, resourceType, onDelete]);

	const getCellValue = useCallback(
		(quoteTableId: number, valueColumnName: string): number => {
			// Live values override API data
			const liveVal = liveCellValues?.[quoteTableId]?.[valueColumnName];
			if (liveVal !== undefined) {
				return liveVal;
			}
			if (!data?.cells) {
				return 0;
			}
			const cell = data.cells.find(
				(c) => c.quoteTableId === quoteTableId && c.valueColumnName === valueColumnName
			);
			return cell?.total ?? 0;
		},
		[data, liveCellValues]
	);

	// Compute live row totals, column totals, and grand total
	const liveTotals = useMemo(() => {
		if (!liveCellValues) {
			return null;
		}
		const rowTotals: Record<number, number> = {};
		const columnTotals: Record<string, number> = {};
		let grandTotal = 0;

		for (const qtId of quoteTableIds) {
			let rowSum = 0;
			for (const cat of categoryNames) {
				const val = getCellValue(qtId, cat);
				rowSum += val;
				columnTotals[cat] = (columnTotals[cat] ?? 0) + val;
			}
			rowTotals[qtId] = rowSum;
			grandTotal += rowSum;
		}

		return { rowTotals, columnTotals, grandTotal };
	}, [liveCellValues, quoteTableIds, categoryNames, getCellValue]);

	// Use live totals when available, otherwise fall back to API data
	const effectiveRowTotals = liveTotals?.rowTotals ?? data?.rowTotals ?? {};
	const effectiveGrandTotal = liveTotals?.grandTotal ?? data?.grandTotal ?? 0;

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-8"
				data-testid={`summary-table-loading-${summaryTableId}`}
			>
				<Spinner size="md" />
			</div>
		);
	}

	return (
		<div
			className="mb-6 rounded-xl border border-[#EAECF0] bg-white shadow-sm"
			data-testid={`summary-table-${summaryTableId}`}
		>
			<div className="flex items-center justify-between border-b border-[#EAECF0] px-4 py-3">
				<Text className="font-roboto text-sm font-semibold text-gray-900">
					{summaryTableName}
				</Text>
				{isProcurerView && (
					<div className="flex items-center gap-1">
						{onEdit && (
							<button
								type="button"
								className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
								onClick={onEdit}
								title="Edit summary table"
								data-testid={`edit-summary-table-${summaryTableId}`}
							>
								<FiEdit2 size={14} />
							</button>
						)}
						<button
							type="button"
							className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
							onClick={handleDelete}
							disabled={isDeleting}
							title="Delete summary table"
							data-testid={`delete-summary-table-${summaryTableId}`}
						>
							<FiTrash2 size={14} />
						</button>
					</div>
				)}
			</div>
			<div className="overflow-x-auto">
				<table
					className="w-full table-auto"
					data-testid={`summary-table-grid-${summaryTableId}`}
				>
					<thead>
						<tr className="bg-[#F9FAFB]">
							<th
								className={cn(
									cellBorder,
									"px-4 py-2 text-left font-roboto text-xs font-medium text-tertiary-600"
								)}
								style={{ minWidth: "180px" }}
							>
								Quote Table
							</th>
							{categoryNames.map((cat) => (
								<th
									key={cat}
									className={cn(
										cellBorder,
										"px-4 py-2 text-right font-roboto text-xs font-medium text-tertiary-600"
									)}
									style={{ minWidth: "120px" }}
								>
									{cat}
								</th>
							))}
							<th
								className={cn(
									cellBorder,
									"bg-gray-100 px-4 py-2 text-right font-roboto text-xs font-semibold text-gray-900"
								)}
								style={{ minWidth: "120px" }}
							>
								Total
							</th>
						</tr>
					</thead>
					<tbody>
						{quoteTableIds.map((qtId) => (
							<tr key={qtId} className="border-b border-[#EAECF0]">
								<td
									className={cn(
										cellBorder,
										"px-4 py-2 font-roboto text-sm text-gray-900"
									)}
								>
									{quoteTableNames[qtId] ?? `Table ${qtId}`}
								</td>
								{categoryNames.map((cat) => (
									<td
										key={`${qtId}-${cat}`}
										className={cn(
											cellBorder,
											"px-4 py-2 text-right font-roboto text-sm text-gray-900"
										)}
										data-testid={`cell-${qtId}-${cat}`}
									>
										{formatCurrency(getCellValue(qtId, cat))}
									</td>
								))}
								<td
									className={cn(
										cellBorder,
										"bg-gray-50 px-4 py-2 text-right font-roboto text-sm font-medium text-gray-900"
									)}
									data-testid={`row-total-${qtId}`}
								>
									{formatCurrency(effectiveRowTotals[qtId] ?? 0)}
								</td>
							</tr>
						))}
						{includeColumnTotals && data && (
							<tr className="border-t-2 border-[#EAECF0] bg-gray-100">
								<td
									className={cn(
										cellBorder,
										"px-4 py-2 font-roboto text-sm font-semibold text-gray-700"
									)}
								>
									Column Totals
								</td>
								{categoryNames.map((cat) => (
									<td
										key={`col-total-${cat}`}
										className={cn(
											cellBorder,
											"px-4 py-2 text-right font-roboto text-sm font-semibold text-gray-700"
										)}
										data-testid={`col-total-${cat}`}
									>
										{formatCurrency(data?.columnTotals?.[cat] ?? 0)}
									</td>
								))}
								<td
									className={cn(
										cellBorder,
										"bg-gray-100 px-4 py-2 text-right font-roboto text-sm font-bold text-gray-900"
									)}
									data-testid="col-total-grand"
								>
									{formatCurrency(effectiveGrandTotal)}
								</td>
							</tr>
						)}
						{mode !== "edit" && TOTAL_ROWS.map((total) => {
							let value = 0;
							if (total.id === TotalType.SubTotal) {
								value = effectiveGrandTotal;
							} else if (total.id === TotalType.GST) {
								value = effectiveGrandTotal * 0.1;
							} else if (total.id === TotalType.Total) {
								value = effectiveGrandTotal + effectiveGrandTotal * 0.1;
							}
							// spacerColSpan = all columns except the last two (label + value)
							const spacerColSpan = Math.max(1, categoryNames.length);
							return (
								<tr key={total.id}>
									<td
										colSpan={spacerColSpan}
										className="h-[48px] border-none"
										style={{ background: "#F9FAFB" }}
									/>
									<td
										className={cn(cellBorder, "bg-gray-utility-100 px-4 py-2")}
										style={{ color: "#D0D5DD" }}
									>
										<div
											className="font-roboto font-medium"
											style={{ fontSize: "12px", color: total.color }}
										>
											{total.label}
										</div>
									</td>
									<td
										className={cn(
											cellBorder,
											"bg-gray-utility-100 px-4 py-2 text-right font-roboto text-sm"
										)}
										style={{ color: "#333" }}
										data-testid={`summary-total-${total.id}`}
									>
										{formatCurrency(value)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export { SummaryTable };
